import { useTranslation } from "react-i18next";

import { Button } from "../../ui";
import type { ChatThreadSummary } from "../../../store/aiChatStore";

export interface ChatHistoryPanelProps {
  threads: ChatThreadSummary[];
  activeThreadId: string | null;
  isLoading: boolean;
  isOpening: boolean;
  error: string | null;
  onOpen: (id: string) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}

const relativeLabel = (updatedAt: number | null, locale: string): string => {
  if (!updatedAt) return "";

  const minutes = Math.round((Date.now() - updatedAt) / 60000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (minutes < 60) return formatter.format(-minutes, "minute");
  if (minutes < 60 * 24) return formatter.format(-Math.round(minutes / 60), "hour");
  return formatter.format(-Math.round(minutes / (60 * 24)), "day");
};

export default function ChatHistoryPanel({
  threads,
  activeThreadId,
  isLoading,
  isOpening,
  error,
  onOpen,
  onRemove,
  onClose,
}: ChatHistoryPanelProps) {
  const { t, i18n } = useTranslation("common");

  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-surface">
      <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
        <p className="text-[13px] font-bold text-content">
          {t("monitoreoIa.chat.history.title")}
        </p>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClose}
          title={t("monitoreoIa.chat.history.close")}
          aria-label={t("monitoreoIa.chat.history.close")}
          icon={<i className="ri-close-line text-lg" />}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading && (
          <p className="py-6 text-center text-xs text-content-muted">
            {t("monitoreoIa.chat.history.loading")}
          </p>
        )}

        {!isLoading && error && (
          <div className="rounded-xl border border-error/30 bg-error/10 p-3">
            <p className="text-xs text-content">{error}</p>
          </div>
        )}

        {!isLoading && !error && threads.length === 0 && (
          <div className="py-8 text-center">
            <i className="ri-chat-history-line text-2xl text-content-faint" />
            <p className="mt-2 text-xs text-content-muted">
              {t("monitoreoIa.chat.history.empty")}
            </p>
          </div>
        )}

        <ul className="space-y-1.5">
          {threads.map((thread) => (
            <li key={thread.id}>
              <div
                className={`group flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                  thread.id === activeThreadId
                    ? "border-primary-500/40 bg-primary-500/10"
                    : "border-line bg-card hover:bg-subtle"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onOpen(thread.id)}
                  disabled={isOpening}
                  className="min-w-0 flex-1 text-left disabled:opacity-60"
                >
                  <p className="truncate text-xs font-medium text-content">{thread.title}</p>
                  <p className="mt-0.5 text-[10px] text-content-faint">
                    {relativeLabel(thread.updated_at, i18n.language)}
                  </p>
                </button>

                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  onClick={() => onRemove(thread.id)}
                  title={t("monitoreoIa.chat.history.delete")}
                  aria-label={t("monitoreoIa.chat.history.delete")}
                  className="shrink-0 text-content-faint hover:text-error"
                  icon={<i className="ri-delete-bin-line" />}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
